/**
 * The caller's raw resource-grant state, as context.
 *
 * ⛔ WHY THIS EXISTS AND WHY IT IS NOT `usePermissionSnapshot()`. `buildPermissionSnapshot`
 * collapses an unusable `resourceGrants` from `null` to `[]`, which is CORRECT for permission
 * *checks* — a check must fail closed. But it destroys exactly the distinction §9.2 depends on:
 * through the snapshot, "we could not determine your resource access" and "you hold no grants"
 * are the same value. A surface that renders an empty list from a failed fetch is the
 * `absent → denied` collapse this release exists to delete.
 *
 * So the UN-collapsed pair travels separately, and it travels TOGETHER — `grants` and `error` are
 * one object for the same reason they are one object in `grants-state.ts`.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { GrantsState } from './grants-state';
import type { GrantsError } from './ZeroAccessPanel';

const GrantsStateCtx = createContext<GrantsState | null>(null);

export function GrantsStateProvider({
  value,
  children,
}: {
  readonly value: GrantsState;
  readonly children: ReactNode;
}): React.JSX.Element {
  return <GrantsStateCtx value={value}>{children}</GrantsStateCtx>;
}

/**
 * The raw grant state.
 *
 * Throws outside a provider rather than defaulting: a default of `{grants: [], error: null}`
 * would silently claim "loaded, none" for a subtree that was never told anything — the same
 * collapse this module exists to prevent, reintroduced as a convenience default.
 */
export function useGrantsState(): GrantsState {
  const ctx = useContext(GrantsStateCtx);
  if (ctx === null) throw new Error('useGrantsState must be used within a GrantsStateProvider');
  return ctx;
}

/**
 * The raw grant state, or `null` outside a `GrantsStateProvider`.
 *
 * Consumers: `AppScopeRoute`, which renders under `CpAdminApp`'s provider in the console and with no
 * provider in the route-table tests, and `AppCatalogView`, which decides the reach of the
 * availability cells from it. `null` means that no grant state reaches this tree. It never
 * means "loaded, none": a caller that needs that answer reads {@link useGrantsState}, which throws.
 */
export function useOptionalGrantsState(): GrantsState | null {
  return useContext(GrantsStateCtx);
}

const GrantsReloadCtx = createContext<(() => void) | null>(null);

/**
 * Carries the control that reads the caller's grants (`GET /v1/auth/me/grants`) again.
 *
 * Provider: `CpAdminApp`, which owns the one grants read. Consumer: `NewAppPane`, after
 * `POST /v1/apps` made the caller the owner of a new app (D7 of 2026-10-20-cp-permissioning). The
 * shell reads the grants once per identity, so without a second read the new app's tabs stay
 * hidden until a page reload.
 *
 * `value` must keep one identity across renders. The re-read keeps the previous grants on screen
 * until the new answer lands, so no frame claims the caller holds nothing. `error` is the failure
 * of the last re-read beside those kept grants, `null` while a re-read runs and after every other
 * answer (R-015 item 4). It defaults to `null` for a tree that starts no re-read of its own.
 */
export function GrantsReloadProvider({
  value,
  pending,
  error = null,
  children,
}: {
  readonly value: () => void;
  /** `true` while a re-read that `value` started has not landed. */
  readonly pending: boolean;
  /** The failure of the last re-read beside the kept grants; `null` while a re-read runs. */
  readonly error?: GrantsError | null;
  readonly children: ReactNode;
}): React.JSX.Element {
  const reread = useMemo<GrantsReread>(() => ({ pending, reload: value, error }), [pending, value, error]);
  return (
    <GrantsReloadCtx value={value}>
      <GrantsRereadCtx value={reread}>{children}</GrantsRereadCtx>
    </GrantsReloadCtx>
  );
}

/** The state of the grants re-read, for a surface that waits on it. */
export interface GrantsReread {
  /** `true` while a re-read has not landed. */
  readonly pending: boolean;
  /** Reads the caller's grants again. */
  readonly reload: () => void;
  /**
   * The failure of the last re-read when the grants on screen are the previous usable list, else
   * `null`. Always `null` while `pending` is `true`.
   */
  readonly error: GrantsError | null;
}

const GrantsRereadCtx = createContext<GrantsReread | null>(null);

/**
 * The grants re-read state, or `null` outside a `GrantsReloadProvider`.
 *
 * Consumers: `AppScopeRoute`, which shows a status while the re-read after a create runs, and a
 * retry when the new app's tabs did not arrive; and `AuthenticatedShell`, which shows the failure
 * of the re-read from `error`. `null` is honest outside the provider: no control
 * there can start a re-read, so none is in flight.
 */
export function useGrantsReread(): GrantsReread | null {
  return useContext(GrantsRereadCtx);
}

/**
 * The control that reads the caller's grants again.
 *
 * Throws outside a provider, for the reason {@link useGrantsState} does: a silent no-op default
 * would leave a new app hidden while the code that asked for the re-read believes it ran.
 */
export function useGrantsReload(): () => void {
  const ctx = useContext(GrantsReloadCtx);
  if (ctx === null) throw new Error('useGrantsReload must be used within a GrantsReloadProvider');
  return ctx;
}
